import React, { FC, useEffect, useState } from 'react';
import { Questionnaire } from '@typings/Questionnaire';
import { NumberInput, Select } from '@components/DesignLibrary/Atoms';
import { useFormEditorContext } from '@components/Questionnaire/questionnaireEditor/FormEditor/FormEditorContext';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useToast } from '@hooks';
import Spacer from '@components/Spacer';
import Buttons from '@components/Buttons';
import Button from '@components/DesignLibrary/Button';
import InputField from '@components/FormFields/InputField';
import PencilSimple from '@components/Icons/PencilSimple';
import Modal from '@components/Modal';
import getListOrTableField from '@helpers/getListOrTableField';
import s from './PDFFillEditor.module.scss';

type Props = {
  field: Questionnaire.Field;
};

const PDFFieldEditor: FC<Props> = ({ field }) => {
  const [openFillEditor, setOpenFillEditor] = useState<number | null>(null);
  const { pdfTemplates, fieldsById } = useFormEditorContext();
  const { getData: getPdfFills } = useNoodleApi(tsClient.questionnaires.getPdfFillsForFieldTemplate);
  const { getData: updatePdfFills } = useNoodleApi(tsClient.questionnaires.updatePdfFillsForFieldTemplate, {
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Successfully updated PDF fill for field template'],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [pdfs, setPdfs] = useState<Array<(QuestionnaireApiModels.PdfTemplate & { fields: Array<QuestionnaireApiModels.PdfFieldTemplate> }) | null>>(
    [],
  );
  const [pdfFields, setFields] = useState<
    Array<{
      pdfFieldId: string;
      startIndex: number | null;
      endIndex: number | null;
      optionMapping: string | null;
    }>
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const getData = async (): Promise<void> => {
      const { data: pdfFills } = await getPdfFills({ fieldTemplateId: field.id, listFieldTemplateId: field.listFieldTemplateId ?? undefined });
      const newPdfs: Array<QuestionnaireApiModels.PdfTemplate & { fields: Array<QuestionnaireApiModels.PdfFieldTemplate> }> = [];
      const newFields: Array<{
        pdfFieldId: string;
        startIndex: number | null;
        endIndex: number | null;
        optionMapping: string | null;
      }> = [];
      pdfFills?.forEach(fill => {
        const pdf = pdfTemplates.find(p => p.key === fill.pdfFieldTemplate.pdfTemplate.key);
        if (pdf) {
          const newField = pdf.fields.find(f => f.id === fill.pdfFieldTemplate.id);
          if (newField) {
            newPdfs.push(pdf);
            newFields.push({
              endIndex: fill.endIndex,
              optionMapping: JSON.stringify(fill.optionMapping, null, 2),
              pdfFieldId: newField.id,
              startIndex: fill.startIndex,
            });
          }
        }
      });
      setPdfs(newPdfs);
      setFields(newFields);
      setIsLoading(false);
    };
    getData();
  }, [field, getPdfFills, pdfTemplates]);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    await updatePdfFills({
      fieldTemplateId: field.id,
      listFieldTemplateId: field.listFieldTemplateId,
      pdfFills: pdfFields.map(f => ({
        ...f,
        optionMapping: f.optionMapping ? JSON.parse(f.optionMapping) : null,
      })),
    });
    setIsSaving(false);
  };

  const addPdfFill = (): void => {
    setPdfs(prev => [...prev, null]);
    let optionMapping: string | null = null;
    if (field.type === 'multi-select') {
      optionMapping = JSON.stringify(
        {
          gladeOptionKey: '',
        },
        null,
        2,
      );
    }
    if (field.type === 'selection' || field.type === 'single-select') {
      optionMapping = JSON.stringify(
        field.options.reduce(
          (acc, val) => ({
            ...acc,
            [val.key]: '',
          }),
          {},
        ),
        null,
        2,
      );
    }
    setFields(prev => [
      ...prev,
      {
        endIndex: null,
        optionMapping,
        pdfFieldId: '',
        startIndex: null,
      },
    ]);
    setOpenFillEditor(pdfs.length);
  };

  const deletePdfFill = (idx: number): void => {
    if (window.confirm(`Are you sure you want to delete this PDF fill?`)) {
      setPdfs(prev => prev.filter((_, pdfIdx) => pdfIdx !== idx));
      setFields(prev => prev.filter((_, fieldIdx) => fieldIdx !== idx));
      setOpenFillEditor(null);
    }
  };

  if (isLoading) {
    return <ProgressIndicator isCentered isPage />;
  }

  return (
    <>
      {pdfs.length
        ? <table className={s.fills}>
          <thead>
            <tr>
              <th>PDF</th>
              <th>Field</th>
              <th className={s.edit}></th>
            </tr>
          </thead>
          <tbody>
            {pdfFields.map((pdfField, idx) => (
              <tr key={idx}>
                <td>{pdfs[idx] && !pdfs[idx]?.fields && <ProgressIndicator />}{pdfTemplates.find(pdf => pdf.key === pdfs[idx]?.key)?.name || 'N/A'}</td>
                <td>{pdfs[idx]?.fields.find(
                  f => f.id === pdfField.pdfFieldId,
                )?.label || 'N/A'}</td>
                <td className={s.edit} title="Edit">
                  <Buttons isWrapper onClick={() => setOpenFillEditor(idx)}>
                    <PencilSimple weight="fill" color="var(--color-gray-75)" size={12} />
                  </Buttons>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        : <></>}
      {openFillEditor !== null && <Modal onClose={() => setOpenFillEditor(null)} title="Edit PDF Fill">
        <div className={s.modal}>
          <Select
            label="PDF"
            compact
            placeholder="Select PDF"
            options={pdfTemplates.map(pdfTemplate => ({ key: pdfTemplate.key, label: pdfTemplate.name }))}
            value={pdfs[openFillEditor]?.key}
            onChange={key => setPdfs(prev => prev.map((it, pdfIdx) => (openFillEditor === pdfIdx
              ? pdfTemplates.find(p => p.key === key) ?? null
              : it)))}
          />
          <Spacer size={16} />
          {pdfs[openFillEditor] && !pdfs[openFillEditor]?.fields && <ProgressIndicator />}
          <Select
            label="Field"
            compact
            placeholder="Select Field"
            options={[
              ...(pdfs[openFillEditor]?.fields?.map(f => ({
                key: f.id,
                label: f.label,
              })) ?? []),
            ]}
            value={pdfFields[openFillEditor].pdfFieldId}
            onChange={newField => setFields(prev => prev.map((it, fieldIdx) => (fieldIdx === openFillEditor ? { ...it, pdfFieldId: newField } : it)))}
            disabled={!pdfs[openFillEditor]}
          />
          <Spacer size={16} />
          {getListOrTableField({ fieldId: field.id, fieldsById }) && (
            <>
              <NumberInput
                description="Start index"
                value={pdfFields[openFillEditor].startIndex}
                onChange={newIndex => setFields(prev => prev.map((it, fieldIdx) => (fieldIdx === openFillEditor
                  ? { ...it, startIndex: newIndex }
                  : it)))}
              />
              <Spacer size={16} />
              <NumberInput
                description="End index"
                value={pdfFields[openFillEditor].endIndex}
                onChange={newIndex => setFields(prev => prev.map((it, fieldIdx) => (fieldIdx === openFillEditor
                  ? { ...it, endIndex: newIndex }
                  : it)))}
              />
              <Spacer size={16} />
            </>
          )}
          {(field.options?.length || 0) > 0 && (
            <>
              <InputField
                label="Option mapping"
                rows={6}
                id="optionMapping"
                name="optionMapping"
                values={{ optionMapping: pdfFields[openFillEditor]?.optionMapping ?? '' }}
                onChange={optionMapping => setFields(prev => prev.map((it, fieldIdx) => (fieldIdx === openFillEditor
                  ? { ...it, optionMapping }
                  : it)))}
              />
              <Spacer size={16} />
            </>
          )}
          <Button size='md' fullWidth variant='primary' onClick={() => setOpenFillEditor(null)}>
            Done
          </Button>
          <Spacer size={16} />
          <Button size='md' fullWidth variant='destructive' onClick={() => deletePdfFill(openFillEditor)}>
            Remove PDF fill
          </Button>
        </div>
      </Modal>}
      <Button size="sm" variant="secondary" onClick={addPdfFill}>
        Add PDF fill
      </Button>
      <Button loading={isSaving} size="sm" variant="primary" onClick={handleSave}>
        Save Field
      </Button>
    </>
  );
};

export default PDFFieldEditor;
